import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Card, Col, Container, Form,
  Row, Spinner, Toast, ToastContainer,
} from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowDown, FaArrowUp, FaPlus, FaSave, FaTimesCircle, FaTrash } from 'react-icons/fa';
import {
  createSetTemplate,
  getSetTemplateDetail,
  updateSetTemplate,
} from '@/api/reviewSetApi';
import { getTemplates } from '@/api/adminApi';
import type {
  CreateReviewSetTemplateRequest,
  ReviewSetTemplateItemInputDto,
} from '@/types/reviews';

type ReviewTemplateOption = {
  templateGuid: string;
  templateName: string;
  templateCategory: string;
};

type ItemRow = ReviewSetTemplateItemInputDto & { _key: number };

let _keyCounter = 0;
const nextKey = () => ++_keyCounter;

const emptyItem = (): ItemRow => ({
  _key: nextKey(),
  reviewTemplateGuid: '',
  sortOrder: 0,
  isRequired: true,
});

const ReviewSetTemplateEditor: React.FC = () => {
  const { setTemplateGuid } = useParams<{ setTemplateGuid: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(setTemplateGuid);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toastMsg, setToastMsg] = useState('');
  const [toastVariant, setToastVariant] = useState<'success' | 'danger'>('success');
  const [showToast, setShowToast] = useState(false);

  // Header fields
  const [setTemplateName, setSetTemplateName] = useState('');
  const [setTemplateCategory, setSetTemplateCategory] = useState('');
  const [setTemplateDescription, setSetTemplateDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [activeInstanceCount, setActiveInstanceCount] = useState(0);

  // Items
  const [items, setItems] = useState<ItemRow[]>([emptyItem()]);

  // Available review templates to pick from
  const [availableTemplates, setAvailableTemplates] = useState<ReviewTemplateOption[]>([]);

  const showToastMsg = (msg: string, variant: 'success' | 'danger' = 'success') => {
    setToastMsg(msg);
    setToastVariant(variant);
    setShowToast(true);
  };

  const loadAvailableTemplates = useCallback(async () => {
    try {
      const data = await getTemplates();
      setAvailableTemplates(
        data
          .filter((t: any) => t.isActive)
          .map((t: any) => ({
            templateGuid: t.templateGuid,
            templateName: t.templateName,
            templateCategory: t.category,
          }))
      );
    } catch {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    loadAvailableTemplates();

    if (!isEdit) {
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const detail = await getSetTemplateDetail(setTemplateGuid!);
        setSetTemplateName(detail.setTemplateName);
        setSetTemplateCategory(detail.setTemplateCategory);
        setSetTemplateDescription(detail.setTemplateDescription ?? '');
        setIsActive(detail.isActive);
        setActiveInstanceCount(detail.activeInstanceCount);
        setItems(
          detail.items.map((i) => ({
            _key: nextKey(),
            reviewTemplateGuid: i.reviewTemplateGuid,
            sortOrder: i.sortOrder,
            isRequired: i.isRequired,
          }))
        );
      } catch {
        setError('Failed to load set template.');
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, setTemplateGuid, loadAvailableTemplates]);

  const addItem = () => {
    setItems((prev) => [...prev, emptyItem()]);
  };

  const removeItem = (key: number) => {
    setItems((prev) => prev.filter((i) => i._key !== key));
  };

  const updateItem = (key: number, patch: Partial<ReviewSetTemplateItemInputDto>) => {
    setItems((prev) => prev.map((i) => (i._key === key ? { ...i, ...patch } : i)));
  };

  const moveItem = (key: number, direction: 'up' | 'down') => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i._key === key);
      if (idx === -1) return prev;
      const next = direction === 'up' ? idx - 1 : idx + 1;
      if (next < 0 || next >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[next]] = [copy[next], copy[idx]];
      return copy;
    });
  };

  const handleSave = async () => {
    setError(null);

    if (!setTemplateName.trim()) {
      setError('Set template name is required.');
      return;
    }
    if (!setTemplateCategory.trim()) {
      setError('Category is required.');
      return;
    }
    if (items.length === 0) {
      setError('At least one review template must be added.');
      return;
    }
    if (items.some((i) => !i.reviewTemplateGuid)) {
      setError('All items must have a review template selected.');
      return;
    }

    const payload = {
      setTemplateName: setTemplateName.trim(),
      setTemplateCategory: setTemplateCategory.trim(),
      setTemplateDescription: setTemplateDescription.trim() || undefined,
      isActive,
      items: items.map((i, idx) => ({
        reviewTemplateGuid: i.reviewTemplateGuid,
        sortOrder: idx + 1,
        isRequired: i.isRequired,
      })),
      sessionGuid: localStorage.getItem('sessionGuid') ?? undefined,
    };

    try {
      setSaving(true);
      if (isEdit) {
        await updateSetTemplate(setTemplateGuid!, payload);
        showToastMsg('Set template updated.');
      } else {
        const result = await createSetTemplate(payload as CreateReviewSetTemplateRequest);
        showToastMsg('Set template created.');
        setTimeout(() => navigate(`/admin/review-sets/templates/${result.setTemplateGuid}/edit`), 800);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err.message ?? 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="mt-4 pb-5">
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast bg={toastVariant} show={showToast} onClose={() => setShowToast(false)} delay={3000} autohide>
          <Toast.Body className="text-white fw-semibold">{toastMsg}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="d-flex align-items-center gap-2 mb-3">
        <Button variant="link" className="p-0 text-secondary" onClick={() => navigate('/admin/review-sets/templates')}>
          ← Set Templates
        </Button>
      </div>

      <h4 className="mb-4">{isEdit ? 'Edit Review Set Template' : 'New Review Set Template'}</h4>

      {isEdit && activeInstanceCount > 0 && (
        <Alert variant="warning" className="mb-3">
          <strong>{activeInstanceCount}</strong> active review set{activeInstanceCount !== 1 ? 's' : ''} use
          this template. Changes to items will apply to <em>new</em> assignments only.
        </Alert>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Header */}
      <Card className="mb-4">
        <Card.Header>
          <strong>Set Details</strong>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  value={setTemplateName}
                  onChange={(e) => setSetTemplateName(e.target.value)}
                  placeholder="e.g. Annual Site Review"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Category <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  value={setTemplateCategory}
                  onChange={(e) => setSetTemplateCategory(e.target.value)}
                  placeholder="e.g. Annual Review"
                />
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Form.Check
                type="switch"
                id="isActive"
                label="Active"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={setTemplateDescription}
                  onChange={(e) => setSetTemplateDescription(e.target.value)}
                  placeholder="Optional description shown to reviewers"
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Items */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Review Template Items</strong>
          <Badge bg="secondary">{items.length} item{items.length !== 1 ? 's' : ''}</Badge>
        </Card.Header>
        <Card.Body className="p-0">
          {items.length === 0 && (
            <div className="p-3 text-secondary fst-italic">No items added yet.</div>
          )}
          {items.map((item, idx) => {
            const tpl = availableTemplates.find((t) => t.templateGuid === item.reviewTemplateGuid);
            return (
              <div
                key={item._key}
                className="d-flex align-items-center gap-2 px-3 py-2 border-bottom"
                style={{ background: 'var(--bg-raised)' }}
              >
                <span className="text-secondary me-1" style={{ width: 24, flexShrink: 0, textAlign: 'right' }}>
                  {idx + 1}.
                </span>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <Form.Select
                    size="sm"
                    value={item.reviewTemplateGuid}
                    onChange={(e) => updateItem(item._key, { reviewTemplateGuid: e.target.value })}
                  >
                    <option value="">— select review template —</option>
                    {availableTemplates.map((t) => (
                      <option key={t.templateGuid} value={t.templateGuid}>
                        {t.templateName} ({t.templateCategory})
                      </option>
                    ))}
                  </Form.Select>
                </div>

                {tpl && (
                  <Badge bg="info" text="dark" className="d-none d-md-inline">
                    {tpl.templateCategory}
                  </Badge>
                )}

                <Form.Check
                  type="switch"
                  id={`req-${item._key}`}
                  label="Required"
                  checked={item.isRequired}
                  onChange={(e) => updateItem(item._key, { isRequired: e.target.checked })}
                  className="ms-1 text-nowrap"
                />

                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-secondary"
                  onClick={() => moveItem(item._key, 'up')}
                  disabled={idx === 0}
                  title="Move up"
                >
                  <FaArrowUp />
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-secondary"
                  onClick={() => moveItem(item._key, 'down')}
                  disabled={idx === items.length - 1}
                  title="Move down"
                >
                  <FaArrowDown />
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-danger"
                  onClick={() => removeItem(item._key)}
                  title="Remove"
                >
                  <FaTrash />
                </Button>
              </div>
            );
          })}
        </Card.Body>
        <Card.Footer>
          <Button variant="outline-secondary" size="sm" onClick={addItem}>
            <FaPlus className="me-1" />
            Add Review Template
          </Button>
        </Card.Footer>
      </Card>

      {/* Actions */}
      <div className="d-flex gap-2">
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size="sm" className="me-1" /> : <FaSave className="me-1" />}
          {isEdit ? 'Save Changes' : 'Create Set Template'}
        </Button>
        <Button variant="outline-secondary" onClick={() => navigate('/admin/review-sets/templates')} disabled={saving}>
          <FaTimesCircle className="me-1" />
          Cancel
        </Button>
      </div>
    </Container>
  );
};

export default ReviewSetTemplateEditor;
